import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

/**
 * A category page's Space pick, held for the visit only (Tasks, Routines, Goals).
 *
 * A link may open the page already narrowed (`?workspace=<slug>`, resolved to `seedId` by the page): that
 * is where the visit starts. A pick the reader makes is never written to the URL, and the link's parameter
 * leaves it at that moment, so a refresh or a return to the page starts again at "All Spaces" — a filter is
 * a look at the list, not a place to come back to.
 */
export function useSessionSpaceFilter(seedId: string | undefined, enabled: boolean): [string, (id: string) => void] {
  const router = useRouter();
  const [space, setSpace] = useState(() => (enabled ? seedId ?? '' : ''));
  // The seed can land after mount — a statically optimized page renders before the router has its query and
  // the roster resolves the slug. It still seeds the visit then, unless the reader has picked already.
  const picked = useRef(false);
  useEffect(() => {
    if (enabled && seedId && !picked.current) setSpace(seedId);
  }, [enabled, seedId]);
  const pick = (id: string) => {
    picked.current = true;
    setSpace(id);
    if (typeof router.query.workspace !== 'string') return;
    const query = { ...router.query };
    delete query.workspace;
    void router.replace({ pathname: router.pathname, query }, undefined, { shallow: true, scroll: false });
  };
  return [space, pick];
}
